import { isValidPhone } from '@privacy-bee/validate/phone';
import type { AddressValue } from '~/components/shared/forms/AddressFields.vue';
import type { PhoneValue } from '~/components/shared/forms/PhoneFields.vue';

/** A phone number as every employee-creation endpoint takes it. */
export interface PhonePayload {
    country_code: string;
    phone_number: string;
}

/** An address as the employee family endpoints take it, under the `address_` prefix. */
export interface PrefixedAddressPayload {
    address_line_1?: string;
    address_line_2?: string;
    address_city: string;
    address_region: string;
    address_postal_code?: string;
    address_country: string;
}

/** An address as the employee-create endpoint takes it, in the form's own field names. */
export interface PlainAddressPayload {
    line_1?: string;
    line_2?: string;
    city: string;
    region: string;
    postal_code?: string;
    country: string;
}

/**
 * Phone and address on these forms are optional, and the backend's
 * required-with rules reject a half-filled one — so the rule for "filled in
 * enough to send" lives here once. Three modals build the same two objects, and
 * each copy of the rule is one more place it can drift apart from the others.
 */

/** The phone, or nothing when it is blank or not a phone number. */
export const optionalPhonePayload = (phone: Partial<PhoneValue>): PhonePayload | undefined => {
    const number = phone.phone_number ?? '';
    if (!phone.country_code || !isValidPhone(number)) return undefined;

    return { country_code: phone.country_code, phone_number: number.replace(/\D/g, '') };
};

/**
 * Whether the address names a place the backend will accept: city, region and
 * country are what its required-with rules demand of each other.
 */
const isSendableAddress = (address: Partial<AddressValue>): boolean =>
    !!(address.city && address.region && address.country);

/** The address under the `address_` prefix, or nothing when it is half-filled. */
export const optionalPrefixedAddressPayload = (
    address: Partial<AddressValue>,
): PrefixedAddressPayload | undefined => {
    if (!isSendableAddress(address)) return undefined;

    return {
        address_line_1: address.line_1 || undefined,
        address_line_2: address.line_2 || undefined,
        address_city: address.city!,
        address_region: address.region!,
        address_postal_code: address.postal_code || undefined,
        address_country: address.country!,
    };
};

/** The address in the form's own field names, or nothing when it is half-filled. */
export const optionalAddressPayload = (
    address: Partial<AddressValue>,
): PlainAddressPayload | undefined => {
    if (!isSendableAddress(address)) return undefined;

    return {
        line_1: address.line_1 || undefined,
        line_2: address.line_2 || undefined,
        city: address.city!,
        region: address.region!,
        postal_code: address.postal_code || undefined,
        country: address.country!,
    };
};
